import useMediaQuery from 'use-media-antd-query/es/useMediaQuery';
import { WhDatePicker } from 'src/ui/DatePicker/WhDatePicker';

import React, { useContext, useMemo } from 'react';
import { Space, Form, Select } from 'src/antd';
import { DeleteOutlined } from '@ant-design/icons';
import { T } from '@transifex/react';
import { useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';
import { uniqBy } from 'lodash';
import { WasteFractionTag } from 'src/ui';
import { VALIDATION_MESSAGE } from '../../../../../../../../utils/validation';

import SelectableContainersList from '../../../../../../../../new-components/SelectableContainersList';
import OrderOverview from '../../../../../../../shared/OrderOverview';
import { PropertyContext } from '../../../../../../../../context/property-context';

import { useGetPropertyContainers } from '../../../../../../../../api/property/property-containers';
import { useGetPropertyTicketPrices } from '../../../../../../../../api/property/ticket-prices';
import { ChangeFromTo, PrefferedDate } from '../../elements/Summary';
import { PreferredDateStyles } from '../../styles';
import { StopPropagationDiv } from '../../../../../../../shared/StopPropagationDiv';
import { createMarkUp } from '../../../../../../../../utils/createMarkup';

import { useTicketAvailableDates } from '../useTicketAvailableDates';
import { DATE_FORMAT } from '../consts';
import CustomFieldsFormSection from '../CustomFieldsFormSection';

type TContainerTypeOption = { id: string; name: string };

// Pools the tenant's container-type catalog from every source the portal
// already has loaded. `prices.change_container_type` on each container is the
// "correct" source but tenants often don't configure it; mirror the operator
// platform UX by also accepting types referenced via add_container_prices and
// any container currently in use on the property.
const useContainerTypeCatalog = (
  propertyId: string,
  propertyContainers: $TSFixMe
): TContainerTypeOption[] => {
  const { data: ticketPricesResp } = useGetPropertyTicketPrices(propertyId, {
    includeAddContainerPrices: true,
  });

  return useMemo(() => {
    const pool: TContainerTypeOption[] = [];

    ticketPricesResp?.data?.add_container_prices?.forEach((node: $TSFixMe) => {
      if (node?.container_type?.id)
        pool.push({
          id: node.container_type.id,
          name: node.container_type.name,
        });
    });

    propertyContainers?.data?.forEach((node: $TSFixMe) => {
      if (node?.container?.container_type?.id)
        pool.push({
          id: node.container.container_type.id,
          name: node.container.container_type.name,
        });
      node?.prices?.change_container_type?.forEach((p: $TSFixMe) => {
        if (p?.container_type?.id)
          pool.push({
            id: p.container_type.id,
            name: p.container_type.name,
          });
      });
    });

    return uniqBy(pool, (o) => o.id);
  }, [ticketPricesResp, propertyContainers]);
};

const Content = () => {
  const { ticketTypes, id: propertyId } = useContext(PropertyContext);
  const { ticketTypeId } = useParams<{ ticketTypeId: string }>() as {
    ticketTypeId: string;
  };

  const ticketType: $TSFixMe = ticketTypes?.filter(
    (e: { id: string }) => e.id === ticketTypeId
  );

  const { data: propertyContainersResp } = useGetPropertyContainers(
    propertyId,
    {
      includePrices: true,
      pricesForTicketType: ticketTypeId,
    }
  );
  const containerTypeCatalog = useContainerTypeCatalog(
    propertyId,
    propertyContainersResp?.data
  );

  const availableDates = useTicketAvailableDates({
    propertyId,
    ticketTypeId,
  });

  const {
    portalDateHidden,
    portalDateLabel,
    portalDateHelpText,
    portalDateRequired,
    portalDescription,
    portalNoteHidden,
    portalNoteLabel,
    portalNoteHelpText,
    portalNoteRequired,
  } = ticketType?.[0] || {};

  const isMobile = useMediaQuery('(max-width: 996px)');

  return (
    <>
      {portalDescription && (
        <div
          style={{ marginBottom: '1rem' }}
          dangerouslySetInnerHTML={createMarkUp(portalDescription)}
        />
      )}
      <Form.Item
        label={portalNoteLabel || <T _str="Note" />}
        name={['manageContainers', 'note']}
        tooltip={portalNoteHelpText || undefined}
        rules={[
          {
            required: portalNoteRequired,
            message: VALIDATION_MESSAGE.REQUIRED,
          },
        ]}
        hidden={portalNoteHidden}
      >
        <TextArea style={{ marginBottom: '1rem' }} />
      </Form.Item>
      <SelectableContainersList
        isChangeContainerType
        preName={['manageContainers', 'items']}
        renderItem={({ id, data }, checked) => {
          const {
            container: {
              container_id,
              waste_fraction: wasteFraction,
              container_type: {
                name: containerType,
                id: currentContainerTypeId,
              },
            },
          } = data;
          const typeOptions = containerTypeCatalog
            .filter((o) => o.id !== currentContainerTypeId)
            .map((o) => ({ label: o.name, value: o.id }));
          return {
            icon: (
              <WasteFractionTag
                wasteFraction={wasteFraction}
                iconOnly={isMobile}
              />
            ),
            title: <>{wasteFraction.name}</>,
            subtitle: container_id || undefined,
            addonCenter: <T _str="Current: {value}" value={containerType} />,
            addonEnd: (
              <StopPropagationDiv>
                <Form.Item
                  name={['manageContainers', 'items', id, 'containerType']}
                  dependencies={[['manageContainers', 'items', id, 'checked']]}
                  rules={[
                    {
                      required: checked,
                      message: VALIDATION_MESSAGE.REQUIRED,
                    },
                  ]}
                >
                  <Select
                    placeholder={<T _str="Container type" />}
                    style={{ width: '200px' }}
                    disabled={!checked}
                    options={typeOptions}
                  />
                </Form.Item>
              </StopPropagationDiv>
            ),
            addonContent: !portalDateHidden ? (
              <StopPropagationDiv>
                <PreferredDateStyles>
                  <Form.Item
                    label={portalDateLabel || <T _str="Preferred date" />}
                    name={['manageContainers', 'items', id, 'preferredDate']}
                    tooltip={portalDateHelpText || undefined}
                    rules={[
                      {
                        required: portalDateRequired && checked,
                        message: VALIDATION_MESSAGE.REQUIRED,
                      },
                    ]}
                    hidden={portalDateHidden}
                  >
                    <WhDatePicker
                      format={DATE_FORMAT}
                      inputReadOnly
                      disabled={!checked}
                      disabledDate={(date) =>
                        !(
                          availableDates.get(id) ||
                          availableDates.get('default') ||
                          []
                        )?.includes(date.format('YYYY-MM-DD'))
                      }
                    />
                  </Form.Item>
                </PreferredDateStyles>
              </StopPropagationDiv>
            ) : undefined,
          };
        }}
      />
      <CustomFieldsFormSection />
    </>
  );
};

const BreadCrumbTitle = <T _str="Select containers" />;

const Summary = () => {
  const { id: propertyId, ticketTypesMap } = useContext(PropertyContext);
  const { ticketTypeId } = useParams<{ ticketTypeId: string }>() as {
    ticketTypeId: string;
  };
  // Match the Content step's params so React Query dedupes both calls into
  // a single cache entry / network request.
  const { data } = useGetPropertyContainers(propertyId, {
    includePrices: true,
    pricesForTicketType: ticketTypeId,
  });
  const containerTypeCatalog = useContainerTypeCatalog(propertyId, data?.data);
  const containerTypeNameById = useMemo(() => {
    const map = new Map<string, string>();
    containerTypeCatalog.forEach((o) => map.set(o.id, o.name));
    return map;
  }, [containerTypeCatalog]);
  const ticketType = useMemo(
    () => ticketTypesMap?.[ticketTypeId],
    [ticketTypeId, ticketTypesMap]
  );

  const returnContainerById = (id: string) => {
    return data?.data?.filter(
      (container: $TSFixMe) => container?.container?.id === id
    )?.[0];
  };

  const values = Form.useWatch(['manageContainers']);

  const items = Object.keys(values?.items || {})
    .map((containerId) => ({
      ...values?.items?.[containerId],
      id: containerId,
    }))
    .filter((n) => n.checked)
    .map(({ id, pickupSetting, containerType, preferredDate }) => ({
      containerId: id,
      pickupSettingId: pickupSetting,
      containerTypeId: containerType,
      preferredDate,
    }));

  const { portalDateLabel, portalDateHidden } = ticketType;

  const summaryItems = items?.map((item: $TSFixMe) => {
    const container = returnContainerById(item?.containerId);

    const newContainerTypePrice =
      container?.prices?.change_container_type?.filter(
        (price: { container_type: { id: $TSFixMe } }) =>
          price.container_type.id === item.containerTypeId
      )?.[0];

    const oldContainerTypePrice =
      container?.prices?.change_container_type?.filter(
        (price: { container_type: { id: $TSFixMe } }) =>
          price.container_type.id === container?.container?.container_type?.id
      )?.[0];

    const newContainerTypeName =
      newContainerTypePrice?.container_type?.name ||
      containerTypeNameById.get(item.containerTypeId);

    return {
      icon: <DeleteOutlined />,
      header: container?.container?.waste_fraction?.name,
      headerSuffix: container?.container?.container_id,
      description: (
        <Space direction="vertical">
          <ChangeFromTo
            from={container?.container?.container_type?.name}
            to={newContainerTypeName}
          />
          <PrefferedDate
            value={item.preferredDate}
            dateText={portalDateLabel}
            dateHidden={portalDateHidden}
          />
        </Space>
      ),
      price:
        newContainerTypePrice?.price?.container_price > 0
          ? newContainerTypePrice?.price?.container_price / 100
          : 0,
      previousPrice:
        oldContainerTypePrice?.price?.container_price > 0
          ? oldContainerTypePrice?.price?.container_price / 100
          : 0,
      recurringPrice: true,
    };
  });

  return <OrderOverview header="" items={summaryItems} />;
};

export const FormChangeContainerType = {
  Form: { BreadCrumbTitle, Content },
  Summary,
};
